import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import { 
  MessageSquare, 
  Bug, 
  Lightbulb, 
  CreditCard, 
  BookOpen, 
  Settings,
  CheckCircle,
  AlertTriangle
} from "lucide-react";

const feedbackSchema = z.object({
  subject: z.string().min(5, "Subject must be at least 5 characters"),
  category: z.enum(["technical", "content", "billing", "feature_request", "bug_report"]),
  priority: z.enum(["low", "medium", "high", "urgent"]).default("medium"),
  description: z.string().min(20, "Please provide at least 20 characters describing your issue"),
  courseId: z.number().optional(),
  trackId: z.number().optional(),
});

type FeedbackFormData = z.infer<typeof feedbackSchema>;

interface FeedbackFormProps {
  courseId?: number;
  trackId?: number;
  triggerButton?: React.ReactNode;
}

const CATEGORY_CONFIG = {
  technical: {
    icon: Settings,
    label: "Technical Issue",
    description: "Database access, login problems, site errors",
    color: "text-red-600",
    bgColor: "bg-red-50 dark:bg-red-950/20"
  },
  content: {
    icon: BookOpen,
    label: "Content Issue",
    description: "Course materials, videos, missing content",
    color: "text-blue-600",
    bgColor: "bg-blue-50 dark:bg-blue-950/20"
  },
  billing: {
    icon: CreditCard,
    label: "Billing & Payment",
    description: "Subscription issues, payment problems",
    color: "text-green-600",
    bgColor: "bg-green-50 dark:bg-green-950/20"
  },
  feature_request: {
    icon: Lightbulb,
    label: "Feature Request",
    description: "Suggest new features or improvements",
    color: "text-purple-600",
    bgColor: "bg-purple-50 dark:bg-purple-950/20"
  },
  bug_report: {
    icon: Bug,
    label: "Bug Report",
    description: "Something broken or not working correctly",
    color: "text-orange-600",
    bgColor: "bg-orange-50 dark:bg-orange-950/20"
  }
};

export default function FeedbackForm({ courseId, trackId, triggerButton }: FeedbackFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Prevent event bubbling when opening dialog
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setIsSubmitted(false);
    }
  };

  const form = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      subject: "",
      category: "technical",
      priority: "medium",
      description: "",
      courseId,
      trackId,
    },
  });

  const submitFeedback = useMutation({
    mutationFn: async (data: FeedbackFormData) => {
      const browserInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        screen: {
          width: screen.width,
          height: screen.height,
          colorDepth: screen.colorDepth
        },
        timestamp: new Date().toISOString(),
        url: window.location.href
      };

      return await fetch("/api/feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...data,
          email: user?.email || "",
          userAgent: navigator.userAgent,
          browserInfo
        }),
      }).then(res => res.json());
    },
    onSuccess: () => {
      setIsSubmitted(true);
      toast({
        title: "Feedback Submitted",
        description: "Thank you! We'll review your feedback and respond soon.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/feedback"] });
      form.reset();
      
      setTimeout(() => {
        setIsOpen(false);
        setIsSubmitted(false);
      }, 2000);
    },
    onError: (error: any) => {
      toast({
        title: "Submission Failed",
        description: error.message || "Failed to submit feedback. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (data: FeedbackFormData) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to submit feedback.",
        variant: "destructive",
      });
      return;
    }
    submitFeedback.mutate(data);
  };

  const defaultTrigger = (
    <Button variant="outline" className="gap-2">
      <MessageSquare className="w-4 h-4" />
      Report Issue
    </Button>
  );

  if (isSubmitted) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          {triggerButton || defaultTrigger}
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Feedback Submitted</DialogTitle>
            <DialogDescription>
              Thank you for your feedback. We'll review it and respond soon.
            </DialogDescription>
          </DialogHeader>
          <div className="text-center py-6">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Feedback Submitted!</h3>
            <p className="text-muted-foreground">
              We'll review your feedback and get back to you soon.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {triggerButton || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Submit Feedback
          </DialogTitle>
          <DialogDescription>
            Report issues, suggest improvements, or share your experience with our platform.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            {/* Category Selection */}
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Issue Category</FormLabel>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                      const IconComponent = config.icon;
                      const isSelected = field.value === key;
                      
                      return (
                        <div
                          key={key}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            isSelected 
                              ? `border-primary ${config.bgColor}` 
                              : "border-muted hover:border-muted-foreground/50"
                          }`}
                          onClick={() => field.onChange(key)}
                        >
                          <div className="flex items-start gap-3">
                            <IconComponent className={`w-5 h-5 mt-0.5 ${config.color}`} />
                            <div className="flex-1">
                              <div className="font-medium text-sm">{config.label}</div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {config.description}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Subject */}
            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="Brief description of the issue"
                      className="w-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Priority */}
            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority Level</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="low">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          Low - General feedback
                        </div>
                      </SelectItem>
                      <SelectItem value="medium">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          Medium - Standard issue
                        </div>
                      </SelectItem>
                      <SelectItem value="high">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          High - Affecting learning
                        </div>
                      </SelectItem>
                      <SelectItem value="urgent">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                          Urgent - Blocking access
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Detailed Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field}
                      placeholder="Please provide as much detail as possible about the issue you're experiencing. Include steps to reproduce if it's a bug."
                      className="min-h-[120px] resize-none"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Context Information */}
            {(courseId || trackId) && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  This feedback will be associated with{" "}
                  {courseId && `Course ${courseId}`}
                  {courseId && trackId && " in "}
                  {trackId && `Track ${trackId}`}
                </AlertDescription>
              </Alert>
            )}

            {/* User Info Display */}
            {user && (
              <div className="bg-muted/50 p-3 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Feedback will be sent from:</div>
                <div className="font-medium">{user.email}</div>
                <div className="text-sm text-muted-foreground">
                  {user.firstName} {user.lastName} • {user.tier} tier
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={submitFeedback.isPending}
                className="flex-1"
              >
                {submitFeedback.isPending ? "Submitting..." : "Submit Feedback"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={submitFeedback.isPending}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}